import React, { useCallback, useMemo } from 'react';

import dayjs from 'dayjs';
import groupBy from 'lodash/groupBy';

import { EventNote, InsertInvitation, LocalPoliceOutlined } from '@mui/icons-material';
import { Alert, Box, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import Accordion from '~/components/v2/common/Accordion/Accordion';
import ReadOnlyField from '~/components/v2/common/ReadOnlyField/ReadOnlyField';

import { formatDateSlashes } from '~/services/TimeService';

import { sortBy } from '~/utils/arrayUtils';

interface Schedule {
  type: string;
  start: string;
  end: string;
  region?: string;
}

interface BrandSchedules {
  [key: string]: Array<Schedule>;
}

interface ScheduleIndex {
  list_visibility: Array<Schedule>;
  availability: { start: string; end: string };
  online_purchase: { start: string; end: string };
}

interface Props {
  keyDates: App.KeyDates;
  functionalDetails: App.FunctionalDetails;
  isAdmin: boolean;
  offerResponse: any;
  brand: string;
  blackOutDates: Array<{
    name: string;
    from__c: string;
    to__c: string;
    opportunity__c: string;
  }>;
}

export default function OpportunityDatesAndPolicies({
  keyDates,
  functionalDetails,
  isAdmin,
  offerResponse,
  brand,
  blackOutDates,
}: Props) {
  const { expiry_date_travel_from__c, travel_to__c, book_by__c, latest_travel_to__c } = keyDates;
  const {
    days_before_check_in_changes_disallowed__c,
    package_upgrades_allowed__c,
    number_of_date_changes__c,
    cancellation_policy__c,
  } = functionalDetails;

  const isDateInRange = useCallback((startDate: string, endDate: string) => {
    const today = dayjs();
    const start = dayjs(startDate, 'DD/MM/YYYY');
    const end = dayjs(endDate, 'DD/MM/YYYY');
    return (today.isAfter(start) || today.isSame(start)) && (today.isBefore(end) || today.isSame(end));
  }, []);

  const getDaysSinceEnd = useCallback((endDate: string) => {
    const today = dayjs();
    const end = dayjs(endDate, 'DD/MM/YYYY');
    return today.diff(end, 'day');
  }, []);

  const getDaysUntilStart = useCallback((startDate: string) => {
    const today = dayjs();
    const start = dayjs(startDate, 'DD/MM/YYYY');
    return start.diff(today, 'day');
  }, []);

  const schedules = useMemo(
    () => groupBy(offerResponse?.brand_schedules, (s) => s.brand),
    [offerResponse?.brand_schedules],
  );

  const schedulesFor = useCallback((brand: string, brandSchedules: BrandSchedules) => {
    const schedules = { list: [], country_list: [] };
    const schedules_index: ScheduleIndex = {
      list_visibility: [],
      availability: { start: '', end: '' },
      online_purchase: { start: '', end: '' },
    };

    if (!brandSchedules[brand]) {
      return schedules;
    }

    for (const schedule of brandSchedules[brand]) {
      if (schedule.type === 'availability' || schedule.type === 'online_purchase') {
        schedules_index[schedule.type] = schedule;
      } else if (schedule.type === 'list_visibility') {
        schedules_index.list_visibility.push(schedule);
      }
    }

    if (schedules_index.availability) {
      schedules.list.push({
        date: schedules_index.availability.start ? formatDateSlashes(schedules_index.availability.start) : '-',
      });
    }
    if (schedules_index.online_purchase) {
      schedules.list.push({
        date: schedules_index.online_purchase.start ? formatDateSlashes(schedules_index.online_purchase.start) : '-',
      });
      schedules.list.push({
        date: schedules_index.online_purchase.end ? formatDateSlashes(schedules_index.online_purchase.end) : '-',
      });
    }
    if (schedules_index.availability) {
      schedules.list.push({
        date: schedules_index.availability.end ? formatDateSlashes(schedules_index.availability.end) : '-',
      });
    }
    for (const schedule of schedules_index.list_visibility) {
      schedules.country_list.push({
        title: schedule.region,
        dateFrom: schedule.start ? formatDateSlashes(schedule.start) : '-',
        dateTo: schedule.end ? formatDateSlashes(schedule.end) : '-',
      });
    }
    return schedules;
  }, []);

  const brandSchedules = useMemo(() => schedulesFor(brand, schedules), [brand, schedules, schedulesFor]);

  const onlineStartDate = brandSchedules.list[1]?.date;
  const onlineEndDate = brandSchedules.list[2]?.date;
  const offlineStartDate = brandSchedules.list[0]?.date;
  const offlineEndDate = brandSchedules.list[3]?.date;

  const isOnlineLive = useMemo(
    () => onlineStartDate && onlineEndDate && isDateInRange(onlineStartDate, onlineEndDate),
    [onlineStartDate, onlineEndDate, isDateInRange],
  );

  const isOfflineAvailableForSale = useMemo(
    () => offlineStartDate && offlineEndDate && isDateInRange(offlineStartDate, offlineEndDate),
    [offlineStartDate, offlineEndDate, isDateInRange],
  );

  const isOnlineEnded = useMemo(
    () => onlineEndDate && dayjs().isAfter(dayjs(onlineEndDate, 'DD/MM/YYYY')),
    [onlineEndDate],
  );

  const isOfflineEnded = useMemo(
    () => offlineEndDate && dayjs().isAfter(dayjs(offlineEndDate, 'DD/MM/YYYY')),
    [offlineEndDate],
  );

  const isOnlineScheduled = useMemo(
    () => onlineStartDate && dayjs().isBefore(dayjs(onlineStartDate, 'DD/MM/YYYY')),
    [onlineStartDate],
  );

  const isOfflineScheduled = useMemo(
    () => offlineStartDate && dayjs().isBefore(dayjs(offlineStartDate, 'DD/MM/YYYY')),
    [offlineStartDate],
  );

  const daysSinceOnlineEnded = useMemo(
    () => (isOnlineEnded ? getDaysSinceEnd(onlineEndDate) : 0),
    [isOnlineEnded, onlineEndDate, getDaysSinceEnd],
  );

  const daysSinceOfflineEnded = useMemo(
    () => (isOfflineEnded ? getDaysSinceEnd(offlineEndDate) : 0),
    [isOfflineEnded, offlineEndDate, getDaysSinceEnd],
  );

  const daysUntilOnlineStart = useMemo(
    () => (isOnlineScheduled ? getDaysUntilStart(onlineStartDate) : 0),
    [isOnlineScheduled, onlineStartDate, getDaysUntilStart],
  );

  const daysUntilOfflineStart = useMemo(
    () => (isOfflineScheduled ? getDaysUntilStart(offlineStartDate) : 0),
    [isOfflineScheduled, offlineStartDate, getDaysUntilStart],
  );

  return (
    <>
      <Box id="deal-dates" mt={2}>
        <Accordion title="Deal dates" icon={<InsertInvitation />}>
          <ReadOnlyField
            title="Travel from"
            value={expiry_date_travel_from__c ? formatDateSlashes(expiry_date_travel_from__c) : '-'}
            type="text"
          />
          <ReadOnlyField title="Travel to" value={travel_to__c ? formatDateSlashes(travel_to__c) : '-'} type="text" />
          {isAdmin && (
            <ReadOnlyField title="Book by" value={book_by__c ? formatDateSlashes(book_by__c) : '-'} type="text" />
          )}
          <ReadOnlyField
            title="Latest Travel To"
            value={latest_travel_to__c ? formatDateSlashes(latest_travel_to__c) : '-'}
            type="text"
          />

          <Box mt={2}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Black out dates (Salesforce)
            </Typography>
            <Box sx={{ width: '100%' }}>
              <DataGrid
                autoHeight
                density="compact"
                sx={{ width: '100%' }}
                rows={
                  Array.isArray(blackOutDates)
                    ? sortBy(blackOutDates, (row) => row.from__c, 'asc').map((row, idx) => ({
                        id: idx,
                        name: row.name || '-',
                        from: row.from__c ? formatDateSlashes(row.from__c) : '-',
                        to: row.to__c ? formatDateSlashes(row.to__c) : '-',
                      }))
                    : []
                }
                columns={[
                  { field: 'name', headerName: 'Name', flex: 1 },
                  { field: 'from', headerName: 'From Date', flex: 1 },
                  { field: 'to', headerName: 'To Date', flex: 1 },
                ]}
                pageSizeOptions={[5, 10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 5, page: 0 } },
                }}
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                localeText={{
                  noRowsLabel: 'No black out dates.',
                }}
              />
            </Box>
          </Box>
        </Accordion>
      </Box>

      <Box id="run-dates" mt={2}>
        <Accordion title="Run dates" icon={<EventNote />}>
          <ReadOnlyField
            title="Online Run Dates"
            value={
              brandSchedules.list[1]?.date && brandSchedules.list[2]?.date
                ? `${brandSchedules.list[1].date} - ${brandSchedules.list[2].date}`
                : '-'
            }
            type="text"
          />
          <ReadOnlyField
            title="Offline Run Dates"
            value={
              brandSchedules.list[0]?.date && brandSchedules.list[3]?.date
                ? `${brandSchedules.list[0].date} - ${brandSchedules.list[3].date}`
                : '-'
            }
            type="text"
          />
          {isOnlineLive && <Alert severity="success">Live</Alert>}
          {isOfflineAvailableForSale && <Alert severity="success">Available for Sale</Alert>}
          {isOnlineEnded && (
            <Alert severity="warning">
              Ended - {daysSinceOnlineEnded} {daysSinceOnlineEnded === 1 ? 'day' : 'days'} ago
            </Alert>
          )}
          {isOfflineEnded && (
            <Alert severity="warning">
              Ended - {daysSinceOfflineEnded} {daysSinceOfflineEnded === 1 ? 'day' : 'days'} ago
            </Alert>
          )}
          {isOnlineScheduled && (
            <Alert severity="info">
              Scheduled - Starts in {daysUntilOnlineStart} {daysUntilOnlineStart === 1 ? 'day' : 'days'}
            </Alert>
          )}
          {isOfflineScheduled && (
            <Alert severity="info">
              Scheduled - Starts in {daysUntilOfflineStart} {daysUntilOfflineStart === 1 ? 'day' : 'days'}
            </Alert>
          )}
        </Accordion>
      </Box>
      <Box id="policies" mt={2}>
        <Accordion title="Policies" icon={<LocalPoliceOutlined />}>
          <ReadOnlyField
            title="Date change allowed"
            value={
              days_before_check_in_changes_disallowed__c ? days_before_check_in_changes_disallowed__c.toString() : '-'
            }
            type="text"
          />
          <ReadOnlyField
            title="Package upgrade allowed"
            value={package_upgrades_allowed__c.toString() || '-'}
            type="text"
          />
          <ReadOnlyField
            title="Number of date changes allowed"
            value={number_of_date_changes__c ? number_of_date_changes__c.toString() : '-'}
            type="text"
          />
          <ReadOnlyField title="Cancellation Policy" value={cancellation_policy__c || '-'} type="text" />
        </Accordion>
      </Box>
    </>
  );
}
